import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import CheckoutStepper from '../../../components/CheckoutStepper/CheckoutStepper';
import BasketStep from '../Basket/BasketStep';
import ShippingStep from '../Shipping/ShippingStep';
import PaymentStep from '../Payment/PaymentStep';
import OrderSummaryPanel from '../OrderSummaryPanel/OrderSummaryPanel';
import BankTransferStep from '../BankTransferStep/BankTransferStep';
import OrderPlacedStep from './OrderPlacedStep';
import "./CheckoutPage.scss";
import { resetCart } from "../../../Store/reducers/cartReducer";
import useCartStockSync from "../../../hooks/useCartStockSync";

const SHIPPING_FORM_ID = "shipping-form";

const CheckoutPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isGuest = searchParams.get('guest') === 'true';
    const token = Cookies.get("token");
    const dispatch = useDispatch();

    useEffect(() => {
        if (!isGuest && !token) {
            navigate('/login?redirect=/checkout');
        }
    }, [isGuest, token, navigate]);

    const [activeStep, setActiveStep] = useState(0);
    const [shippingData, setShippingData] = useState({});
    const [paymentData, setPaymentData] = useState({});
    // The order as saved by the backend (id, totalAmount, paymentMethod).
    const [placedOrder, setPlacedOrder] = useState(null);
    const basketProducts = useSelector(state => state.cart.products);

    const nextStep = () => setActiveStep(s => Math.min(s + 1, 3));
    const backStep = () => setActiveStep(s => Math.max(s - 1, 0));

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeStep]);

    // Re-check stock on every step; a product that sold out meanwhile sends
    // the shopper back to the basket, where it is flagged.
    useCartStockSync(activeStep, activeStep < 3);
    const hasUnavailable = basketProducts.some((item) => item.unavailable);
    useEffect(() => {
        if (hasUnavailable && activeStep > 0 && activeStep < 3) setActiveStep(0);
    }, [hasUnavailable, activeStep]);

    // Order creation lives in PaymentStep; it calls this once the order
    // exists. PayU orders redirect to PayU instead and never get here.
    const handleOrderCreated = (order) => {
        setPlacedOrder(order);
        dispatch(resetCart());
        setActiveStep(3);
    };

    return (
        <div>
            <CheckoutStepper activeStep={activeStep} />
            <div className="checkout-main-content">
                <div className="checkout-step">
                    {activeStep === 0 && <BasketStep />}
                    {activeStep === 1 && (
                        <ShippingStep
                            formId={SHIPPING_FORM_ID}
                            shippingData={shippingData}
                            setShippingData={setShippingData}
                            onBack={backStep}
                            onNext={nextStep}
                            isGuest={isGuest}
                        />
                    )}
                    {activeStep === 2 && (
                        <PaymentStep
                            shippingData={shippingData}
                            paymentData={paymentData}
                            setPaymentData={setPaymentData}
                            onBack={backStep}
                            onOrderCreated={handleOrderCreated}
                        />
                    )}
                    {activeStep === 3 && placedOrder?.paymentMethod === 'bank_transfer' && (
                        <BankTransferStep
                            orderId={placedOrder.id}
                            totalAmount={Number(placedOrder.totalAmount)}
                        />
                    )}
                    {activeStep === 3 && placedOrder && placedOrder.paymentMethod !== 'bank_transfer' && (
                        <OrderPlacedStep isGuest={isGuest || !token} />
                    )}
                </div>
                {activeStep < 3 && basketProducts.length > 0 && (
                    <aside className="checkout-summary">
                        <OrderSummaryPanel
                            activeStep={activeStep}
                            onNext={nextStep}
                            shippingMethod={paymentData.selectedShipping}
                            shippingFormId={SHIPPING_FORM_ID}
                            blocked={hasUnavailable}
                        />
                    </aside>
                )}
            </div>
        </div>
    );
};

export default CheckoutPage;
